import { useState } from 'react';
import { FileText, Save, Download, Plus, Loader2, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useToast } from '../components/ui/Toast';
import { useMsdsCoverpage } from '../hooks/useMsdsCoverpage';
import { PdfUploader } from '../components/msds-coverpage/PdfUploader';
import { MakBatTable } from '../components/msds-coverpage/MakBatTable';
import { HStatementWarnings } from '../components/msds-coverpage/HStatementWarnings';
import { SavedCoverpagesList } from '../components/msds-coverpage/SavedCoverpagesList';

function SectionCard({ title, subtitle, children, defaultOpen = true }: {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="bg-white rounded-lg border shadow-sm">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 p-4 text-left hover:bg-gray-50 transition-colors"
            >
                {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <div>
                    <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
                    {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
                </div>
            </button>
            {open && <div className="px-4 pb-4">{children}</div>}
        </div>
    );
}

function FormField({ label, required, children }: {
    label: string;
    required?: boolean;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {children}
        </div>
    );
}

export default function MsdsCoverpagePage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/msds-coverpage');
    const { toast } = useToast();
    const {
        formData,
        hStatements,
        hCheckResult,
        savedCoverpages,
        currentId,
        isExtracting,
        isLookingUpMak,
        isSaving,
        isGenerating,
        error,
        saveName,
        updateField,
        updateSaveName,
        updateMakBatValue,
        extractPdf,
        saveCoverpage,
        generateDocument,
        loadCoverpage,
        resetForm,
    } = useMsdsCoverpage();

    const [activeTab, setActiveTab] = useState<'new' | 'saved'>('new');

    if (permLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    const handleSave = async () => {
        if (!formData.tradeName.trim()) {
            toast('Trade name is required', 'error');
            return;
        }
        try {
            const result = await saveCoverpage();
            toast(result.isNew ? 'Cover page saved' : 'Cover page updated', 'success');
        } catch {
            toast('Failed to save', 'error');
        }
    };

    const handleGenerate = async (format: 'docx' | 'pdf') => {
        if (!formData.tradeName.trim()) {
            toast('Trade name is required to generate document', 'error');
            return;
        }
        try {
            await generateDocument(format);
            toast(`${format.toUpperCase()} downloaded`, 'success');
        } catch {
            toast('Failed to generate document', 'error');
        }
    };

    const handleLoadSaved = async (id: number) => {
        try {
            await loadCoverpage(id);
            setActiveTab('new');
            toast('Cover page loaded', 'info');
        } catch {
            toast('Failed to load cover page', 'error');
        }
    };

    const inputClass = 'w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none';
    const textareaClass = `${inputClass} resize-y`;

    return (
        <div className="p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-blue-600" />
                    <div>
                        <h1 className="text-lg font-bold text-gray-900">MSDS Cover Page</h1>
                        <p className="text-xs text-gray-500">Generate Swiss SDB Deckblatt from MSDS documents</p>
                    </div>
                </div>
                {canEdit && (
                    <button
                        onClick={() => { resetForm(); setActiveTab('new'); }}
                        className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Create New
                    </button>
                )}
            </div>

            {/* Error banner */}
            {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 mb-4">
                <button
                    onClick={() => setActiveTab('new')}
                    className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        activeTab === 'new'
                            ? 'bg-blue-100 text-blue-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    {currentId ? 'Edit Cover Page' : 'New Cover Page'}
                </button>
                <button
                    onClick={() => setActiveTab('saved')}
                    className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        activeTab === 'saved'
                            ? 'bg-blue-100 text-blue-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    Saved ({savedCoverpages.length})
                </button>
            </div>

            {/* Saved list tab */}
            {activeTab === 'saved' && (
                <div className="bg-white rounded-lg border shadow-sm p-4">
                    <SavedCoverpagesList coverpages={savedCoverpages} onLoad={handleLoadSaved} />
                </div>
            )}

            {/* Form tab */}
            {activeTab === 'new' && (
                <div className="space-y-4">
                    {/* PDF Upload */}
                    <SectionCard title="Upload MSDS PDF" subtitle="Upload the original Safety Data Sheet for AI-powered data extraction">
                        <PdfUploader onUpload={extractPdf} isProcessing={isExtracting} />
                        {isExtracting && (
                            <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                AI is analyzing the document and extracting data...
                            </p>
                        )}
                    </SectionCard>

                    {/* Header info */}
                    <SectionCard title="Cover Page Header">
                        <div className="grid grid-cols-2 gap-4">
                            <FormField label="Cover page date" required>
                                <input
                                    type="text"
                                    value={formData.coverpageDate}
                                    onChange={e => updateField('coverpageDate', e.target.value)}
                                    className={inputClass}
                                    placeholder="DD.MM.YYYY"
                                />
                            </FormField>
                            <FormField label="Replaces version from">
                                <input
                                    type="text"
                                    value={formData.replacesVersionFrom || ''}
                                    onChange={e => updateField('replacesVersionFrom', e.target.value)}
                                    className={inputClass}
                                    placeholder="DD.MM.YYYY (optional)"
                                />
                            </FormField>
                        </div>
                    </SectionCard>

                    {/* Product Identification */}
                    <SectionCard title="Product Identification" subtitle="Section 1">
                        <div className="space-y-3">
                            <FormField label="Trade Name" required>
                                <input
                                    type="text"
                                    value={formData.tradeName}
                                    onChange={e => updateField('tradeName', e.target.value)}
                                    className={inputClass}
                                    placeholder="Product name from SDS"
                                />
                            </FormField>
                            <FormField label="Usage Purpose">
                                <input
                                    type="text"
                                    value={formData.usagePurpose}
                                    onChange={e => updateField('usagePurpose', e.target.value)}
                                    className={inputClass}
                                    placeholder="Identified uses"
                                />
                            </FormField>
                            <FormField label="SDS Version / Date">
                                <input
                                    type="text"
                                    value={formData.sdsVersion}
                                    onChange={e => updateField('sdsVersion', e.target.value)}
                                    className={inputClass}
                                    placeholder="Version date of original SDS"
                                />
                            </FormField>
                        </div>
                    </SectionCard>

                    {/* Supplier (fixed) */}
                    <SectionCard title="Supplier" subtitle="Dolder AG (fixed)" defaultOpen={false}>
                        <div className="space-y-3">
                            <FormField label="Company Name">
                                <input type="text" value={formData.supplierName} onChange={e => updateField('supplierName', e.target.value)} className={inputClass} />
                            </FormField>
                            <FormField label="Address">
                                <textarea value={formData.supplierAddress} onChange={e => updateField('supplierAddress', e.target.value)} className={textareaClass} rows={2} />
                            </FormField>
                            <div className="grid grid-cols-2 gap-4">
                                <FormField label="Phone">
                                    <input type="text" value={formData.supplierPhone} onChange={e => updateField('supplierPhone', e.target.value)} className={inputClass} />
                                </FormField>
                                <FormField label="Email">
                                    <input type="text" value={formData.supplierEmail} onChange={e => updateField('supplierEmail', e.target.value)} className={inputClass} />
                                </FormField>
                            </div>
                        </div>
                    </SectionCard>

                    {/* Emergency (fixed) */}
                    <SectionCard title="Emergency Number" subtitle="145 - Tox Info Suisse (fixed)" defaultOpen={false}>
                        <FormField label="Emergency Number">
                            <input type="text" value={formData.emergencyNumber} onChange={e => updateField('emergencyNumber', e.target.value)} className={inputClass} />
                        </FormField>
                    </SectionCard>

                    {/* Section 7 */}
                    <SectionCard title="Section 7 - Handling & Storage">
                        <FormField label="Swiss handling and storage requirements">
                            <textarea
                                value={formData.section7Notes}
                                onChange={e => updateField('section7Notes', e.target.value)}
                                className={textareaClass}
                                rows={3}
                                placeholder="Additional Swiss requirements for handling and storage..."
                            />
                        </FormField>
                    </SectionCard>

                    {/* Section 8 - MAK/BAT */}
                    <SectionCard title="Section 8 - Swiss Workplace Limits (SUVA)" subtitle={isLookingUpMak ? 'Looking up SUVA values...' : undefined}>
                        {isLookingUpMak && (
                            <div className="flex items-center gap-2 text-sm text-blue-600 mb-3">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Looking up Swiss MAK/BAT values from SUVA...
                            </div>
                        )}
                        <MakBatTable values={formData.makBatValues as any} onUpdate={updateMakBatValue} />
                        <div className="mt-3">
                            <FormField label="Personal protective equipment notes">
                                <textarea
                                    value={formData.section8PsaNotes}
                                    onChange={e => updateField('section8PsaNotes', e.target.value)}
                                    className={textareaClass}
                                    rows={2}
                                    placeholder="Additional PSA requirements..."
                                />
                            </FormField>
                        </div>
                    </SectionCard>

                    {/* Section 13 */}
                    <SectionCard title="Section 13 - Disposal" defaultOpen={false}>
                        <FormField label="Swiss disposal requirements">
                            <textarea
                                value={formData.section13Notes}
                                onChange={e => updateField('section13Notes', e.target.value)}
                                className={textareaClass}
                                rows={3}
                            />
                        </FormField>
                    </SectionCard>

                    {/* Section 15 */}
                    <SectionCard title="Section 15 - Legal Requirements">
                        <div className="space-y-4">
                            <HStatementWarnings result={hCheckResult} hCodes={hStatements} />

                            <FormField label="Additional legal references">
                                <textarea
                                    value={formData.section15Notes}
                                    onChange={e => updateField('section15Notes', e.target.value)}
                                    className={textareaClass}
                                    rows={3}
                                    placeholder="Swiss-specific legal requirements..."
                                />
                            </FormField>

                            <FormField label="Mengenschwelle (Störfallverordnung)">
                                <input
                                    type="text"
                                    value={formData.mengenschwelle}
                                    onChange={e => updateField('mengenschwelle', e.target.value)}
                                    className={inputClass}
                                    placeholder="Optional - pending info from Lab BS"
                                />
                            </FormField>
                        </div>
                    </SectionCard>

                    {/* Save & Generate */}
                    {canEdit && (
                        <div className="bg-white rounded-lg border shadow-sm p-4 space-y-3">
                            <FormField label="Save Name">
                                <input
                                    type="text"
                                    value={saveName}
                                    onChange={e => updateSaveName(e.target.value)}
                                    className={inputClass}
                                    placeholder={formData.tradeName || 'Enter a name for this cover page'}
                                />
                                <p className="text-xs text-gray-400 mt-1">Defaults to Trade Name. Change to use a custom save name.</p>
                            </FormField>
                            <div className="flex items-center gap-3">
                            <button
                                onClick={handleSave}
                                disabled={isSaving || !formData.tradeName.trim()}
                                className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Save Draft
                            </button>
                            <button
                                onClick={() => handleGenerate('docx')}
                                disabled={isGenerating || !formData.tradeName.trim()}
                                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                Generate Word
                            </button>
                            <button
                                onClick={() => handleGenerate('pdf')}
                                disabled={isGenerating || !formData.tradeName.trim()}
                                className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                Generate PDF
                            </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
